import { useState, useEffect } from "react";
import axios from "axios";
import { Megaphone, Loader2, AlertTriangle, Info, AlertOctagon, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

var API = "/api";

var DEFAULT_PAYMENT_DOWN_MESSAGE = "Payment processing is temporarily down. To place an order, please reach out to us directly at admin@resurrectedgunparts.com — we apologize for the inconvenience and will have it back up shortly.";

var SEVERITY_OPTIONS = [
  { value: "info", label: "Info", color: "bg-[#3a3a3a] text-white", Icon: Info, hint: "Neutral gray — for announcements, new arrivals" },
  { value: "warning", label: "Warning", color: "bg-[#C0A062] text-black", Icon: AlertTriangle, hint: "Brass — for service issues, delays" },
  { value: "critical", label: "Critical", color: "bg-red-600 text-white", Icon: AlertOctagon, hint: "Red — for outages, urgent issues" }
];

export default function SiteSettingsTab() {
  var [enabled, setEnabled] = useState(false);
  var [message, setMessage] = useState("");
  var [severity, setSeverity] = useState("warning");
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);

  // Payments toggle state
  var [paypalEnabled, setPaypalEnabled] = useState(false);
  var [creditcardEnabled, setCreditcardEnabled] = useState(true);
  var [paymentsMessage, setPaymentsMessage] = useState("");
  var [savingPayments, setSavingPayments] = useState(false);

  useEffect(function() {
    Promise.all([
      axios.get(API + "/site-settings/banner").then(function(res) {
        setEnabled(res.data.enabled);
        setMessage(res.data.message || "");
        setSeverity(res.data.severity || "warning");
      }),
      axios.get(API + "/site-settings/payments").then(function(res) {
        setPaypalEnabled(res.data.paypal_enabled === true);
        setCreditcardEnabled(res.data.creditcard_enabled !== false);
        setPaymentsMessage(res.data.disabled_message || "");
      })
    ])
      .catch(function() { toast.error("Failed to load site settings"); })
      .finally(function() { setLoading(false); });
  }, []);

  function handleSave() {
    if (enabled && !message.trim()) {
      toast.error("Please write a message before turning the banner on");
      return;
    }
    setSaving(true);
    axios.put(API + "/site-settings/banner", {
      enabled: enabled,
      message: message.trim(),
      severity: severity
    }, { withCredentials: true })
      .then(function(res) {
        toast.success(res.data.enabled ? "Banner is now live on every page" : "Banner turned off");
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || "Failed to save";
        toast.error(msg);
      })
      .finally(function() { setSaving(false); });
  }

  function handleSavePayments() {
    if (!paymentsMessage.trim()) {
      toast.error("Disabled message cannot be empty");
      return;
    }
    setSavingPayments(true);
    axios.put(API + "/site-settings/payments", {
      paypal_enabled: paypalEnabled,
      creditcard_enabled: creditcardEnabled,
      disabled_message: paymentsMessage.trim()
    }, { withCredentials: true })
      .then(function() {
        var anyOn = paypalEnabled || creditcardEnabled;
        toast.success(anyOn
          ? "Payment settings saved"
          : "ALL online payments are now DISABLED — customers will be directed to contact you directly");
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || "Failed to save";
        toast.error(msg);
      })
      .finally(function() { setSavingPayments(false); });
  }

  function loadPaymentDownTemplate() {
    setMessage(DEFAULT_PAYMENT_DOWN_MESSAGE);
    setSeverity("critical");
    toast.info("Payment-down template loaded. Click Save when ready.");
  }

  function loadHolidayTemplate() {
    setMessage("We're closed for the holiday — orders placed now will ship next business day. Thank you for your patience!");
    setSeverity("info");
    toast.info("Holiday template loaded. Click Save when ready.");
  }

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" /></div>;
  }

  var sevConfig = SEVERITY_OPTIONS.find(function(s) { return s.value === severity; }) || SEVERITY_OPTIONS[1];
  var PreviewIcon = sevConfig.Icon;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* === Payments Toggle === */}
      <div className="bg-[#121212] border border-white/10 rounded-sm p-6 space-y-6">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-[#C0A062] mt-1" />
          <div className="flex-1">
            <h3 className="font-heading font-bold text-white text-lg uppercase">Online Payments</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">Control which checkout methods are available to customers. If you disable both, the checkout page will display a contact message instead.</p>
          </div>
        </div>

        {/* Credit Card (Authorize.Net) */}
        <div className={"flex items-center justify-between p-4 border rounded-sm " + (creditcardEnabled ? "bg-[#1a1a1a] border-[#8CB369]/30" : "bg-red-950/30 border-red-500/30")}>
          <div>
            <Label className="text-white">Credit / Debit Card (Authorize.Net) — {creditcardEnabled ? "Enabled" : "Disabled"}</Label>
            <p className="text-[10px] text-[#71717a] mt-1">
              {creditcardEnabled
                ? "Customers can pay with Visa, Mastercard, AmEx, Discover via Authorize.Net."
                : "The card form is hidden on the checkout page."}
            </p>
          </div>
          <Switch checked={creditcardEnabled} onCheckedChange={setCreditcardEnabled} data-testid="creditcard-enabled-toggle" />
        </div>

        {/* PayPal */}
        <div className={"flex items-center justify-between p-4 border rounded-sm " + (paypalEnabled ? "bg-[#1a1a1a] border-[#8CB369]/30" : "bg-[#1a1a1a] border-white/10 opacity-80")}>
          <div>
            <Label className="text-white">PayPal / Venmo — {paypalEnabled ? "Enabled" : "Disabled"}</Label>
            <p className="text-[10px] text-[#71717a] mt-1">
              {paypalEnabled
                ? "PayPal & Venmo buttons appear at checkout alongside the card form."
                : "PayPal is hidden. Re-enable only if Authorize.Net has issues."}
            </p>
          </div>
          <Switch checked={paypalEnabled} onCheckedChange={setPaypalEnabled} data-testid="paypal-enabled-toggle" />
        </div>

        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-xs uppercase">Message Shown When ALL Payments Are Disabled</Label>
          <Textarea
            value={paymentsMessage}
            onChange={function(e) { setPaymentsMessage(e.target.value); }}
            rows={3}
            maxLength={500}
            className="input-tactical resize-none"
            placeholder="e.g. Online payments are temporarily unavailable..."
            data-testid="payments-disabled-message-input"
          />
          <p className="text-[10px] text-[#71717a]">Only shown when both Credit Card and PayPal are turned off.</p>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <p className="text-[10px] text-[#71717a]">
            <span className="font-semibold text-[#A1A1AA]">Tip:</span> Pair this with the announcement banner below for maximum visibility.
          </p>
          <Button
            className="btn-primary"
            onClick={handleSavePayments}
            disabled={savingPayments}
            data-testid="payments-save-btn"
          >
            {savingPayments && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {savingPayments ? "Saving..." : "Save Payment Settings"}
          </Button>
        </div>
      </div>

      {/* === Announcement Banner === */}
      <div className="bg-[#121212] border border-white/10 rounded-sm p-6 space-y-6">
        <div className="flex items-start gap-3">
          <Megaphone className="w-5 h-5 text-[#C0A062] mt-1" />
          <div className="flex-1">
            <h3 className="font-heading font-bold text-white text-lg uppercase">Site-Wide Announcement Banner</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">A scrolling marquee banner displayed at the top of every page. Use it for outages, sales, holiday closures, or urgent notices.</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-[#1a1a1a] border border-white/5 rounded-sm">
          <div>
            <Label className="text-white">Banner Enabled</Label>
            <p className="text-[10px] text-[#71717a] mt-1">{enabled ? "Visitors are seeing this right now." : "Banner is hidden from visitors."}</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="banner-enabled-toggle" />
        </div>

        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-xs uppercase">Banner Message</Label>
          <Textarea
            value={message}
            onChange={function(e) { setMessage(e.target.value); }}
            rows={3}
            maxLength={300}
            placeholder="e.g. Payment processing is temporarily down. Please contact us directly..."
            className="input-tactical resize-none"
            data-testid="banner-message-input"
          />
          <div className="flex justify-between items-center text-[10px] text-[#71717a]">
            <span>This text will scroll horizontally across the top of every page.</span>
            <span>{message.length}/300</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-xs uppercase">Severity / Color</Label>
          <div className="grid grid-cols-3 gap-2">
            {SEVERITY_OPTIONS.map(function(opt) {
              var isActive = severity === opt.value;
              var OptIcon = opt.Icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={function() { setSeverity(opt.value); }}
                  className={"p-3 rounded-sm border transition-all text-left " + (isActive ? "border-[#C0A062] bg-[#1a1a1a]" : "border-white/10 bg-[#121212] hover:border-white/30")}
                  data-testid={"severity-" + opt.value}
                >
                  <div className={"inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-mono uppercase " + opt.color}>
                    <OptIcon className="w-3 h-3" />
                    {opt.label}
                  </div>
                  <p className="text-[10px] text-[#71717a] mt-2">{opt.hint}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Live preview */}
        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-xs uppercase">Live Preview</Label>
          <div className="rounded-sm overflow-hidden border border-white/10">
            <div className={"py-2 overflow-hidden " + sevConfig.color}>
              <div className="animate-marquee whitespace-nowrap flex items-center">
                <span className="mx-8 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider">
                  <PreviewIcon className="w-4 h-4" />
                  {message || "Your message will appear here..."}
                </span>
                <span className="mx-8 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider">
                  <PreviewIcon className="w-4 h-4" />
                  {message || "Your message will appear here..."}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-white/5">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"
              onClick={loadPaymentDownTemplate}
              data-testid="banner-template-payment"
            >
              Payment Down Template
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"
              onClick={loadHolidayTemplate}
              data-testid="banner-template-holiday"
            >
              Holiday Template
            </Button>
          </div>
          <Button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
            data-testid="banner-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {saving ? "Saving..." : "Save Banner"}
          </Button>
        </div>
      </div>
    </div>
  );
}
